import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { imageVariants, type ImageVariantProps } from './image.variants';
import { cn } from '@/hen-ui/utils';

export interface ImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement>,
    ImageVariantProps {
  asChild?: boolean;
}

/**
 * Image component following HEN Design System standards.
 *
 * A semantic img element with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Rounded image
 * <Image
 *   rounded
 *   src="/images/banner.jpg"
 *   alt="Banner image"
 * />
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    { className, rounded, objectFit, asChild, height, width, style, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'img';
    return (
      <Comp
        className={cn(
          imageVariants({ rounded, objectFit }),
          !width && 'w-full',
          className,
        )}
        height={height}
        width={width}
        ref={ref}
        {...props}
      />
    );
  },
);

Image.displayName = 'Image';
